'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { isEventDetailPath } from '@/lib/redesign/screens';
import { FvdErrorScreen, ErrorPrimary } from './FvdErrorScreen';

/**
 * The 404 and error views for the two signed-in areas, rendered by their
 * segment's not-found.tsx and error.tsx. Those sit inside the area's layout,
 * so the rail is already there and these draw only the message in the
 * content column. Where that area renders a page with no rail of its own,
 * they draw the full page instead, so the screen never ends up bare.
 *
 * Each has one way on, back to the area's front page. A failed page goes
 * there with a whole page load (ErrorPrimary `fullLoad`), so it also recovers
 * when the dashboard's own front page is the one that failed.
 */

/**
 * Whether the dashboard draws no rail at this path. Mirrors DashboardShell:
 * onboarding never has one, and on the 2026 shell an event's pages bring
 * their own rail, which a missing or failed page never got to render.
 */
export function dashboardIsBare(pathname: string | null, shell: boolean): boolean {
  if (pathname?.startsWith('/dashboard/onboarding')) return true;
  return shell && isEventDetailPath(pathname);
}

/** The hub draws no shell on its login page. */
export function hubIsBare(pathname: string | null): boolean {
  return pathname === '/hub/login';
}

export function DashboardNotFoundView({ shell }: { shell: boolean }) {
  const pathname = usePathname();
  return (
    <FvdErrorScreen
      inShell={!dashboardIsBare(pathname, shell)}
      title="We can’t find that page"
      body="The link may be old, or the page may have moved. Everything else in your dashboard is where you left it."
      primary={<ErrorPrimary href="/dashboard">Back to your dashboard</ErrorPrimary>}
    />
  );
}

export function DashboardErrorView({ error }: { error: Error & { digest?: string } }) {
  const pathname = usePathname();
  useEffect(() => {
    // The digest is the server's reference; no message or stack is logged.
    console.error('Dashboard route error', error.digest ?? '');
  }, [error]);
  // Without the server flag here, an event's page is treated as having no
  // rail: true on the 2026 shell, and on the old one the full page still reads.
  return (
    <FvdErrorScreen
      inShell={!dashboardIsBare(pathname, true)}
      title="Something went wrong"
      body="This page didn’t load. Go back to your dashboard and try again from there."
      primary={<ErrorPrimary href="/dashboard" fullLoad>Back to your dashboard</ErrorPrimary>}
      footnote={error.digest ? `Reference ${error.digest}` : undefined}
    />
  );
}

export function HubNotFoundView() {
  const pathname = usePathname();
  return (
    <FvdErrorScreen
      inShell={!hubIsBare(pathname)}
      gutter={0}
      lockup="myfitvent"
      title="We can’t find that page"
      body="The link may be old, or the page may have moved. Your races and results are all still here."
      primary={<ErrorPrimary href="/hub/home">Back to myfitvent</ErrorPrimary>}
    />
  );
}

export function HubErrorView({ error }: { error: Error & { digest?: string } }) {
  const pathname = usePathname();
  useEffect(() => {
    console.error('Hub route error', error.digest ?? '');
  }, [error]);
  return (
    <FvdErrorScreen
      inShell={!hubIsBare(pathname)}
      gutter={0}
      lockup="myfitvent"
      title="Something went wrong"
      body="This page didn’t load. Go back to myfitvent and try again from there."
      primary={<ErrorPrimary href="/hub/home" fullLoad>Back to myfitvent</ErrorPrimary>}
      footnote={error.digest ? `Reference ${error.digest}` : undefined}
    />
  );
}
